/**
 * <s-welcome-popup> — homepage first-order discount popup.
 *
 * Opens its <dialog> after data-delay ms (default 15000) or once the visitor has scrolled half the page,
 * whichever comes first. Closing it snoozes it for 30 days (localStorage). After a successful signup the
 * server renders [data-welcome-success]; the popup reopens immediately to show the code and never shows again.
 * Signup errors ([data-welcome-error]) also reopen it. The copy button copies [data-welcome-code].
 */
const KEY = 'fne-welcome-popup';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

class SWelcomePopup extends HTMLElement {
  #ac;
  #timer = 0;
  #opened = false;
  #triggers = new AbortController();

  connectedCallback() {
    this.#ac = new AbortController();
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    const signal = this.#ac.signal;

    this.querySelector('[data-welcome-copy]')?.addEventListener('click', (event) => {
      const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
      const code = this.querySelector('[data-welcome-code]')?.textContent?.trim() ?? '';
      navigator.clipboard?.writeText(code).then(() => {
        if (button.dataset.copied) button.textContent = button.dataset.copied;
      }).catch(() => {});
    }, { signal });

    if (this.querySelector('[data-welcome-success]')) {
      this.#store('subscribed');
      dialog.showModal();
      return;
    }
    if (this.querySelector('[data-welcome-error]')) {
      dialog.showModal();
      return;
    }
    if (!this.#shouldShow()) return;

    dialog.addEventListener('close', () => this.#store(String(Date.now())), { signal });

    const open = () => {
      if (this.#opened || dialog.open || document.querySelector('dialog[open]')) return;
      this.#opened = true;
      this.#triggers.abort();
      this.#store(String(Date.now()));
      this.#cleanup();
      dialog.showModal();
    };
    this.#timer = window.setTimeout(open, Number(this.dataset.delay) || 15000);
    window.addEventListener('scroll', () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= 0.5) open();
    }, { passive: true, signal: this.#triggers.signal });
  }

  disconnectedCallback() {
    this.#cleanup();
    this.#ac?.abort();
    this.#triggers.abort();
  }

  #cleanup() {
    window.clearTimeout(this.#timer);
  }

  #shouldShow() {
    try {
      const value = localStorage.getItem(KEY);
      if (!value) return true;
      if (value === 'subscribed') return false;
      return Date.now() - Number(value) > SNOOZE_MS;
    } catch {
      return true;
    }
  }

  /** @param {string} value */
  #store(value) {
    try { localStorage.setItem(KEY, value); } catch {}
  }
}

if (!customElements.get('s-welcome-popup')) customElements.define('s-welcome-popup', SWelcomePopup);
